import React, { useState, useEffect, useCallback } from 'react';
import { api, fmt } from '../utils/api';
import PropertyForm from './PropertyForm';
import PropertyFeedback from './PropertyFeedback';

export default function RealEstate() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingProperty, setEditingProperty] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const load = useCallback(async () => {
    try {
      const result = await api.getRealEstate();
      setData(result);
    } catch (err) {
      setError(err.message);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSaved = () => {
    setShowForm(false);
    setEditingProperty(null);
    load();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this property? This cannot be undone.')) return;
    setDeletingId(id);
    try {
      await api.deleteProperty(id);
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setDeletingId(null);
    }
  };

  if (error) return <p style={styles.errorText}>{error}</p>;
  if (!data) return <p style={styles.loadingText}>Loading...</p>;

  const { rentals, homestead, portfolioTotals } = data;

  return (
    <div>
      {rentals.length > 0 && (
        <div style={styles.summaryCard}>
          <div style={styles.summaryGrid}>
            <SummaryStat label="Portfolio Value" value={fmt.currency(portfolioTotals.value)} />
            <SummaryStat label="Total Equity" value={fmt.currency(portfolioTotals.equity)} accent />
            <SummaryStat label="Monthly Cash Flow" value={fmt.currency(portfolioTotals.monthlyCashFlow)} accent={portfolioTotals.monthlyCashFlow >= 0} negative={portfolioTotals.monthlyCashFlow < 0} />
            <SummaryStat
              label="Portfolio Cash-on-Cash"
              value={portfolioTotals.portfolioCashOnCash != null ? `${portfolioTotals.portfolioCashOnCash}%` : '—'}
            />
          </div>
        </div>
      )}

      <div style={styles.sectionHeaderRow}>
        <h3 style={styles.sectionHeader}>🏠 Rental Properties</h3>
        <button style={styles.addBtn} onClick={() => { setEditingProperty(null); setShowForm(true); }}>
          + Add Property
        </button>
      </div>

      {rentals.length === 0 ? (
        <p style={styles.emptyText}>No rental properties yet - add one to start tracking cash flow and returns.</p>
      ) : (
        rentals.map((p) => (
          <PropertyCard
            key={p.id}
            property={p}
            expanded={expandedId === p.id}
            onToggle={() => setExpandedId(expandedId === p.id ? null : p.id)}
            onEdit={() => { setEditingProperty(p); setShowForm(true); }}
            onDelete={() => handleDelete(p.id)}
            deleting={deletingId === p.id}
          />
        ))
      )}

      <h3 style={{ ...styles.sectionHeader, marginTop: '1.75rem' }}>🏡 Homestead</h3>
      {homestead ? (
        <HomesteadCard
          property={homestead}
          onEdit={() => { setEditingProperty(homestead); setShowForm(true); }}
          onDelete={() => handleDelete(homestead.id)}
          deleting={deletingId === homestead.id}
        />
      ) : (
        <div style={styles.homesteadPrompt}>
          <p style={styles.emptyText}>Track your own home separately from investment properties.</p>
          <button style={styles.addBtn} onClick={() => { setEditingProperty({ propertyType: 'homestead' }); setShowForm(true); }}>
            + Add Homestead
          </button>
        </div>
      )}

      {showForm && (
        <PropertyForm
          property={editingProperty?.id ? editingProperty : null}
          onClose={() => { setShowForm(false); setEditingProperty(null); }}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}

function SummaryStat({ label, value, accent, negative }) {
  return (
    <div style={styles.summaryStat}>
      <div style={styles.summaryStatLabel}>{label}</div>
      <div style={{ ...styles.summaryStatValue, color: negative ? '#ef4444' : accent ? '#00d4aa' : '#fff' }}>{value}</div>
    </div>
  );
}

function MetricPill({ label, value, tone }) {
  const color = tone === 'good' ? '#00d4aa' : tone === 'bad' ? '#ef4444' : '#9ca3af';
  return (
    <div style={styles.metricPill}>
      <div style={styles.metricPillLabel}>{label}</div>
      <div style={{ ...styles.metricPillValue, color }}>{value}</div>
    </div>
  );
}

function toneForCashOnCash(v) {
  if (v == null) return 'neutral';
  return v >= 8 ? 'good' : v < 4 ? 'bad' : 'neutral';
}
function toneForCashFlow(v) {
  return v >= 0 ? 'good' : 'bad';
}
function toneForDscr(v) {
  if (v == null) return 'neutral';
  return v >= 1.25 ? 'good' : v < 1 ? 'bad' : 'neutral';
}

const RATING_STYLES = {
  bad: { bg: 'rgba(239,68,68,0.15)', border: '#ef4444', color: '#ef4444', emoji: '🔴' },
  ok: { bg: 'rgba(251,191,36,0.15)', border: '#fbbf24', color: '#fbbf24', emoji: '🟡' },
  good: { bg: 'rgba(0,212,170,0.15)', border: '#00d4aa', color: '#00d4aa', emoji: '🟢' },
  great: { bg: 'rgba(167,139,250,0.15)', border: '#a78bfa', color: '#a78bfa', emoji: '🌟' },
  unknown: { bg: 'rgba(107,114,128,0.15)', border: '#6b7280', color: '#9ca3af', emoji: '❔' },
};

function RatingBadge({ rating }) {
  if (!rating) return null;
  const s = RATING_STYLES[rating.rating] || RATING_STYLES.unknown;
  return (
    <span
      style={{ ...styles.ratingBadge, background: s.bg, borderColor: s.border, color: s.color }}
      title={rating.reason}
    >
      {s.emoji} {rating.label}
    </span>
  );
}

function PropertyCard({ property, expanded, onToggle, onEdit, onDelete, deleting }) {
  const m = property.metrics;
  return (
    <div style={styles.propertyCard}>
      <div style={styles.propertyCardTop} onClick={onToggle}>
        <div>
          <div style={styles.propertyAddressRow}>
            <span style={styles.propertyAddress}>{property.address}</span>
            <RatingBadge rating={property.rating} />
          </div>
          <div style={styles.propertySub}>
            {fmt.currency(property.estimatedValue)} value &middot; {fmt.currency(m.equity)} equity
            {property.monthlyRent && <> &middot; {fmt.currency(property.monthlyRent)}/mo rent</>}
          </div>
        </div>
        <span style={styles.expandChevron}>{expanded ? '▲' : '▼'}</span>
      </div>

      <div style={styles.metricRow}>
        <MetricPill label="Cash Flow" value={`${fmt.currency(m.monthlyCashFlow)}/mo`} tone={toneForCashFlow(m.monthlyCashFlow)} />
        <MetricPill label="Cap Rate" value={m.capRate != null ? `${m.capRate}%` : '—'} />
        <MetricPill label="Cash-on-Cash" value={m.cashOnCash != null ? `${m.cashOnCash}%` : '—'} tone={toneForCashOnCash(m.cashOnCash)} />
        <MetricPill label="DSCR" value={m.dscr != null ? m.dscr : '—'} tone={toneForDscr(m.dscr)} />
      </div>

      {expanded && (
        <div style={styles.expandedSection}>
          <div style={styles.expenseBreakdownGrid}>
            <ExpenseLine label="Mortgage P&I" value={m.expenseBreakdown.mortgagePI} />
            <ExpenseLine label="Property Tax" value={m.expenseBreakdown.tax} />
            <ExpenseLine label="Insurance" value={m.expenseBreakdown.insurance} />
            <ExpenseLine label="HOA" value={m.expenseBreakdown.hoa} />
            <ExpenseLine label="Management" value={m.expenseBreakdown.management} />
            <ExpenseLine label="Maintenance" value={m.expenseBreakdown.maintenance} />
            <ExpenseLine label="CapEx Reserve" value={m.expenseBreakdown.capex} />
            <ExpenseLine label="Vacancy Reserve" value={m.expenseBreakdown.vacancy} />
            <ExpenseLine label="Utilities" value={m.expenseBreakdown.utilities} />
            <ExpenseLine label="Other" value={m.expenseBreakdown.other} />
          </div>

          <div style={styles.moreMetricsRow}>
            <MetricPill label="Total ROI (TTM)" value={m.totalROI != null ? `${m.totalROI}%` : '—'} tone={toneForCashOnCash(m.totalROI)} />
            <MetricPill label="Principal Paydown (TTM)" value={fmt.currency(m.principalPaydownTTM)} />
            <MetricPill label="Expense Ratio" value={m.expenseRatio != null ? `${m.expenseRatio}%` : '—'} />
            <MetricPill label="Rent/Value (1% rule)" value={m.onePercentRuleActual != null ? `${m.onePercentRuleActual}%` : '—'} tone={m.onePercentRuleActual >= 1 ? 'good' : 'neutral'} />
          </div>

          {property.notes && <p style={styles.notesText}>{property.notes}</p>}

          <PropertyFeedback propertyId={property.id} />

          <div style={styles.cardActions}>
            <button style={styles.editBtn} onClick={onEdit}>Edit</button>
            <button style={styles.deleteBtn} onClick={onDelete} disabled={deleting}>
              {deleting ? 'Removing...' : 'Remove'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function HomesteadCard({ property, onEdit, onDelete, deleting }) {
  const m = property.metrics;
  return (
    <div style={styles.propertyCard}>
      <div style={styles.propertyCardTop}>
        <div>
          <div style={styles.propertyAddress}>{property.address}</div>
          <div style={styles.propertySub}>
            {fmt.currency(property.estimatedValue)} value &middot; {fmt.currency(m.equity)} equity
          </div>
        </div>
      </div>
      <div style={styles.metricRow}>
        <MetricPill label="Monthly P&I" value={fmt.currency(m.monthlyPI)} />
        <MetricPill label="Loan Balance" value={fmt.currency(m.loanBalance)} />
        <MetricPill label="Monthly Carrying Cost" value={fmt.currency(m.monthlyOperatingExpenses + m.monthlyPI)} />
        {m.appreciationTTM != null && <MetricPill label="Appreciation Since Purchase" value={fmt.currency(m.appreciationTTM)} tone="good" />}
      </div>
      <div style={styles.cardActions}>
        <button style={styles.editBtn} onClick={onEdit}>Edit</button>
        <button style={styles.deleteBtn} onClick={onDelete} disabled={deleting}>
          {deleting ? 'Removing...' : 'Remove'}
        </button>
      </div>
    </div>
  );
}

function ExpenseLine({ label, value }) {
  if (!value) return null;
  return (
    <div style={styles.expenseLine}>
      <span style={styles.expenseLineLabel}>{label}</span>
      <span style={styles.expenseLineValue}>{fmt.currency(value)}/mo</span>
    </div>
  );
}

const styles = {
  loadingText: { color: '#6b7280', fontSize: '0.85rem', textAlign: 'center', padding: '2rem 0' },
  errorText: { color: '#ef4444', fontSize: '0.85rem', textAlign: 'center', padding: '2rem 0' },
  emptyText: { color: '#6b7280', fontSize: '0.85rem' },
  summaryCard: {
    background: '#111827', border: '1px solid #1e2a3a', borderRadius: '16px',
    padding: '1.25rem', marginBottom: '1.5rem',
  },
  summaryGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem' },
  summaryStat: { textAlign: 'center' },
  summaryStatLabel: { color: '#6b7280', fontSize: '0.7rem', fontWeight: '600', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.03em' },
  summaryStatValue: { fontSize: '1.3rem', fontWeight: '700', fontFamily: "'Syne', sans-serif" },
  sectionHeaderRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.85rem' },
  sectionHeader: { color: '#fff', fontSize: '1rem', fontWeight: '700', fontFamily: "'Syne', sans-serif", margin: 0 },
  addBtn: {
    background: 'rgba(0,212,170,0.12)', border: '1px solid #00d4aa', color: '#00d4aa',
    borderRadius: '8px', padding: '7px 14px', fontSize: '0.78rem', fontWeight: '700',
    cursor: 'pointer', fontFamily: "'Syne', sans-serif",
  },
  propertyCard: {
    background: '#111827', border: '1px solid #1e2a3a', borderRadius: '14px',
    padding: '1.1rem', marginBottom: '0.85rem',
  },
  propertyCardTop: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' },
  propertyAddressRow: { display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' },
  propertyAddress: { color: '#fff', fontSize: '0.95rem', fontWeight: '700', fontFamily: "'Syne', sans-serif" },
  ratingBadge: {
    display: 'inline-block', border: '1px solid', borderRadius: '999px',
    padding: '2px 10px', fontSize: '0.7rem', fontWeight: '700', fontFamily: "'Syne', sans-serif",
  },
  propertySub: { color: '#6b7280', fontSize: '0.78rem', marginTop: '3px', fontFamily: "'DM Mono', monospace" },
  expandChevron: { color: '#6b7280', fontSize: '0.75rem' },
  metricRow: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.5rem', marginTop: '1rem' },
  moreMetricsRow: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.5rem', marginTop: '0.75rem' },
  metricPill: {
    background: '#0a0f1e', border: '1px solid #1e2a3a', borderRadius: '8px',
    padding: '8px 6px', textAlign: 'center',
  },
  metricPillLabel: { color: '#6b7280', fontSize: '0.65rem', fontWeight: '600', textTransform: 'uppercase' },
  metricPillValue: { fontSize: '0.9rem', fontWeight: '700', fontFamily: "'Syne', sans-serif", marginTop: '2px' },
  expandedSection: { marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #1e2a3a' },
  expenseBreakdownGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.4rem 1rem' },
  expenseLine: { display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem' },
  expenseLineLabel: { color: '#9ca3af' },
  expenseLineValue: { color: '#d1d5db', fontFamily: "'DM Mono', monospace" },
  notesText: { color: '#9ca3af', fontSize: '0.8rem', marginTop: '0.75rem', fontStyle: 'italic' },
  cardActions: { display: 'flex', gap: '0.5rem', marginTop: '1rem' },
  editBtn: {
    background: 'transparent', border: '1px solid #374151', color: '#9ca3af', borderRadius: '8px',
    padding: '6px 14px', fontSize: '0.75rem', fontWeight: '600', cursor: 'pointer', fontFamily: "'Syne', sans-serif",
  },
  deleteBtn: {
    background: 'transparent', border: '1px solid rgba(239,68,68,0.4)', color: '#ef4444', borderRadius: '8px',
    padding: '6px 14px', fontSize: '0.75rem', fontWeight: '600', cursor: 'pointer', fontFamily: "'Syne', sans-serif",
  },
  homesteadPrompt: {
    background: '#111827', border: '1px dashed #1e2a3a', borderRadius: '14px',
    padding: '1.25rem', textAlign: 'center',
  },
};
